import type { Verification } from '@/features/verifications/types/verification.types';
import type { ContactCounts } from './wizard-progress';

export type ContactsByExperience = Record<string, Verification[]>;

export interface ContactQueryResult {
  data: Verification[] | undefined;
  isError: boolean;
  isFetching?: boolean;
}

export interface CollectedContacts {
  contactCounts: ContactCounts;
  contactsByExperience: ContactsByExperience;
  loadingExperienceIds: string[];
}

// A failed query that is being retried is still loading; only a settled failure is a failure.
function isLoadingResult(result: ContactQueryResult | undefined): boolean {
  if (!result) return true;
  if (result.isError) return Boolean(result.isFetching);
  return result.data === undefined;
}

// Only successfully loaded results are counted: a failed or pending query must
// not look like an experience without reference contacts. An errored query can
// still expose stale cached data, so the error flag is checked first.
const loadedContacts = (result: ContactQueryResult | undefined): Verification[] | undefined =>
  !result || result.isError ? undefined : result.data;

export function collectContactResults(
  experienceIds: string[],
  results: ContactQueryResult[]
): CollectedContacts {
  const contactCounts: ContactCounts = {};
  const contactsByExperience: ContactsByExperience = {};
  const loadingExperienceIds: string[] = [];

  experienceIds.forEach((experienceId, index) => {
    if (isLoadingResult(results[index])) loadingExperienceIds.push(experienceId);
    const contacts = loadedContacts(results[index]);
    if (!contacts) return;
    contactsByExperience[experienceId] = contacts;
    contactCounts[experienceId] = contacts.length;
  });

  return { contactCounts, contactsByExperience, loadingExperienceIds };
}
